/* App.tsx */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Root from './pages/root';
import './App.css';
import { pingLogin } from './utils/lrs';

/* ───────── Selectors ───────── */
const selectSessionUser = (s: any) => s.session?.user ?? null;
const selectApiToken    = (s: any) =>
  s.auth?.sessionToken ?? s.session?.apiToken ?? null;

/* ───────── Debug fetch wrapper ───────── */
async function fetchJsonDebug<T = any>(
  url:  string,
  init?: RequestInit,
): Promise<T> {
  const res  = await fetch(url, init);
  const text = await res.text();
  console.group(`🛰  ${url}`);
  console.log('status',   res.status, res.statusText);
  console.log('headers',  Array.from(res.headers.entries()));
  console.log('body',     text.slice(0,120) + (text.length>120 ? ' …' : ''));
  console.groupEnd();
  return JSON.parse(text) as T;
}

/* ───────── Utility: extract values from /chk_session HTML ───────── */
function parseSessionHtml(html: string): {
  name?: string;
  mail?: string;
  user?: string;
} {
  const get = (key: string) => {
    const m = html.match(new RegExp(`${key}=([^&<]*)`));
    return m && m[1] ? decodeURIComponent(m[1]) : undefined;
  };
  return {
    name: get('name'),
    mail: get('mail'),
    user: get('user'),
  };
}

/* ───────── React component ───────── */
function App() {
  const dispatch    = useDispatch();
  const sessionUser = useSelector(selectSessionUser);
  const reduxToken  = useSelector(selectApiToken);

  /* Log redux‑token changes */
  useEffect(() => {
    console.log('🪵 [REDUX] sessionToken now:', reduxToken);
  }, [reduxToken]);

  /* Startup flow – runs once */
  useEffect(() => {
    console.group('[APP MOUNTED]');
    console.log('🪵 [LOCALSTORAGE] apiToken at mount:',
      localStorage.getItem('apiToken'));

    /* 1️⃣  Session ping */
    console.log('[SESSION FETCH] GET /chk_session');
    fetch('/chk_session', {
      method:      'GET',
      credentials: 'include',
      cache:       'no-store',
    })
      .then(r => r.text())
      .then(async html => {
        console.log('[SESSION CHECK RAW TEXT]', html);
        const { name, mail, user } = parseSessionHtml(html);

        /* 2️⃣  Decide what we call “userId” & “userName” */
        const userId   =
          sessionUser?.id ?? localStorage.getItem('userId') ?? mail ?? user;
        const userName =
          localStorage.getItem('userName') ?? name ?? sessionUser?.name ?? '';

        if (!userId) {
          console.warn('[USER ID] Still missing after /chk_session');
        } else {
          localStorage.setItem('userId',   userId);
          if (userName) localStorage.setItem('userName', userName);

          /* fire the saga watching RECORD_USER_LOGIN_SAGA */
          dispatch({
            type:    'RECORD_USER_LOGIN_SAGA',
            payload: { userId, userName, userExists: true },
          });

          /* 3️⃣  Sync PHP session */
          console.log('[SESSION INIT] POST userId → set_user_id.php:', userId);
          await fetch('/yisumaticaApiZvi/set_user_id.php', {
            method:      'POST',
            credentials: 'include',
            headers:     { 'Content-Type': 'application/x-www-form-urlencoded' },
            body:        new URLSearchParams({ user_id: userId }),
          });
        }

        /* 4️⃣  Fetch / create token */
        console.log('[TOKEN FETCH] GET → get_or_create_token.php');
        return fetchJsonDebug<{ token?: string; status?: string }>(
          '/yisumaticaApiZvi/get_or_create_token.php',
          {
            method:      'GET',
            credentials: 'include',
            cache:       'no-store',
            headers:     { Accept: 'application/json' },
          },
        );
      })
      .then(data => {
        if (data?.token) {
          localStorage.setItem('apiToken', data.token);
          dispatch({ type: 'SET_API_TOKEN', payload: data.token });
          dispatch({ type: 'GET_TOTAL_LIKES_SAGA' });
          console.log(`[TOKEN] ${data.status ?? 'received'} token:`, data.token);

          /* 5️⃣  LRS login ping */
          const userId   = localStorage.getItem('userId')   ?? '';
          const schoolId = localStorage.getItem('semelMosad') ?? '';
          pingLogin(userId, schoolId)
            .then(() => console.log('[LRS] login ping ✓'))
            .catch(e  => console.warn('[LRS] login ping ✗', e));
        } else {
          console.warn('[TOKEN] No token in server response');
        }
      })
      .catch(err => console.error('Startup flow error:', err))
      .finally(() => console.groupEnd());
  }, [dispatch, sessionUser]);

  return <Root />;
}

export default App;
